<script setup lang="ts">
import type { Video } from '@ilteducation/videos';
import { useLocale } from '@/plugins/translation';
import { useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';
import type { Area, Category } from '@ilteducation/content-ref-data-types';

type Props = {
  areaVideos: Video[];
  video: Video;
  area: Area;
  subject: Category;
  showHeader?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
});

const emits = defineEmits<{
  (e: 'click-video'): void;
}>();

const { t, l } = useLocale();
const route = useRoute();
const router = useRouter();

const areaGuide = computed(() => {
  if (props.area?.guide?.active) {
    return {
      id: 'area-guide',
      title: `${t(l.area_guide.greppa)} ${props.area.name}`,
      path: `/videos/${props.subject?.slug}/${props.area?.slug}`,
      icon: 'fa-regular fa-rocket',
    };
  }
  return null;
});

const clickAreaGuide = () => {
  if (areaGuide.value) {
    router.push(areaGuide.value.path);
  }
};

const clickVideo = (video: Video) => {
  emits('click-video');
  router.push({
    name: 'showFilm',
    params: {
      subject: route.params.subject,
      area: route.params.area,
      film: video.slug,
    },
  });
};
</script>

<template>
  <v-col
    v-if="areaVideos?.length"
    tag="nav"
    :aria-label="t(l.film.filmsInAreaNamed, { area: area?.name ?? '' })"
    cols="12"
    md="4"
    class="related-films"
  >
    <h2 v-if="showHeader" id="heading-films-in-area" class="title subheader mb-2">
      {{ t(l.film.filmsInAreaNamed, { area: area?.name ?? '' }) }}
    </h2>
    <v-list
      class="movie-list pb-16 bg-transparent ga-2 d-flex flex-column"
      variant="plain"
      role="list"
      aria-labelledby="heading-films-in-area"
    >
      <!-- Get started -->
      <li v-if="area?.guide?.active">
        <v-list-item class="movie-list-item rounded" @click="clickAreaGuide">
          <template #prepend>
            <FontAwesomeIcon :icon="areaGuide!.icon" class="mr-4" />
          </template>
          {{ areaGuide!.title }}
        </v-list-item>
      </li>
      <!-- Videos in area -->
      <li v-for="av in areaVideos" :key="av.id">
        <v-list-item class="movie-list-item rounded" :class="{ active: av.id === video.id }" @click="clickVideo(av)">
          <template #prepend>
            <FontAwesomeIcon v-if="av.id === video.id" icon="fa-solid fa-video" bg="primary" class="mr-3" />
            <FontAwesomeIcon v-else icon="fa-regular fa-video" class="mr-4" />
          </template>
          {{ av.title }}
        </v-list-item>
      </li>
    </v-list>
  </v-col>
</template>

<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.title {
  color: rgb(var(--v-theme-on-surface));
}

.related-films {
  position: relative;

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    height: 400px;
    overflow-y: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(to top, rgba(var(--v-theme-surface-container-lowest)), transparent 15%);
    }
  }

  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    height: 500px;
  }
}

.movie-list {
  position: relative;

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    max-height: 100%;
  }
}

.movie-list-item {
  color: rgb(var(--v-theme-on-surface));
  opacity: 1;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.08);
  }

  &.active {
    color: rgb(var(--v-theme-on-secondary)) !important;
    background-color: rgb(var(--v-theme-secondary));
  }
}
</style>
